// /app/frontend/src/pages/ElitePerksPage.jsx
/**
 * Elite Perks Page
 * ================
 * Public-facing page explaining Elite membership benefits.
 * Works for logged-out (marketing) and logged-in (upsell) users.
 * Canonical URL: /elite
 */

import React, { useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { AuthContext } from '../App';
import { 
  Crown, Video, MessageCircle, Sparkles, Shield, Zap, 
  Clock, Users, Heart, ArrowLeft, Check
} from 'lucide-react';
import { 
  PLAN_ENTITLEMENTS, 
  formatLoungeTime, 
  formatSpeedDatingTime 
} from '../config/planEntitlements';

const PERKS = [
  {
    icon: Video,
    title: `${formatSpeedDatingTime(PLAN_ENTITLEMENTS.elite.speedDatingMinutesPerMonth)} Event Time`,
    description: "Participate in structured video introduction events with timed conversations.",
    highlight: true,
  },
  {
    icon: Clock,
    title: `${formatLoungeTime(PLAN_ENTITLEMENTS.elite.loungeMinutesPerDay)} Video Lounge`,
    description: "Get paired into private 1-on-1 video conversations inside themed lounge rooms.",
  },
  {
    icon: MessageCircle,
    title: "Enhanced Messaging",
    description: "Higher daily message limits and additional conversation features.",
  },
  {
    icon: Sparkles,
    title: "AI Conversation Coach",
    description: "Get personalized conversation suggestions and profile tips.",
    highlight: true,
  },
  {
    icon: Heart,
    title: "See Who's Interested",
    description: "View profiles that have expressed interest in connecting with you.",
  },
  {
    icon: Shield,
    title: "Verified Badge",
    description: "Display an identity-verified badge on your profile.",
  },
  {
    icon: Users,
    title: "Priority Visibility",
    description: "Enhanced placement in profile discovery and matching.",
  },
  {
    icon: Zap,
    title: "Profile Boost",
    description: "Weekly visibility boost included with your membership.",
  },
];

// Build tiers dynamically from canonical config
const TIERS = [
  {
    name: PLAN_ENTITLEMENTS.elite.name,
    price: PLAN_ENTITLEMENTS.elite.priceDisplay,
    period: PLAN_ENTITLEMENTS.elite.period,
    loungeTime: formatLoungeTime(PLAN_ENTITLEMENTS.elite.loungeMinutesPerDay),
    speedDating: formatSpeedDatingTime(PLAN_ENTITLEMENTS.elite.speedDatingMinutesPerMonth),
    popular: false,
  },
  {
    name: PLAN_ENTITLEMENTS.elite_plus.name,
    price: PLAN_ENTITLEMENTS.elite_plus.priceDisplay,
    period: PLAN_ENTITLEMENTS.elite_plus.period,
    loungeTime: formatLoungeTime(PLAN_ENTITLEMENTS.elite_plus.loungeMinutesPerDay),
    speedDating: formatSpeedDatingTime(PLAN_ENTITLEMENTS.elite_plus.speedDatingMinutesPerMonth),
    popular: true,
  },
];

export default function ElitePerksPage() {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  
  // Check if user is already Elite (or admin - admins never see upsell)
  const isAdmin = user?.is_admin || user?.roles?.includes('admin');
  const isElite = isAdmin || user?.entitlements?.elite?.active || user?.entitlements?.elite_plus?.active;
  
  return (
    <div className="min-h-screen relative bg-white dark:bg-slate-950 text-slate-900 dark:text-slate-50">
      <Helmet>
        <title>Elite Membership | UnityLove</title>
        <meta name="description" content="Upgrade to UnityLove Elite for enhanced video rooms, speed dating events, AI conversation coaching, and premium features." />
        <link rel="canonical" href="https://joinunitylove.com/elite" />
      </Helmet>
      {/* Contrast overlay (does NOT affect text) */}
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-white via-white to-slate-50 dark:from-slate-950 dark:via-slate-950 dark:to-slate-900/40" />
      
      <div className="relative">
      {/* Header */}
      <nav className="bg-white/80 dark:bg-slate-900/80 backdrop-blur-sm border-b border-gray-200 dark:border-white/10 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Back</span>
            </button>
            
            <div className="flex items-center gap-2">
              <Crown className="w-6 h-6 text-amber-400" />
              <span className="font-playfair text-xl font-bold text-gray-900 dark:text-white">
                Elite Membership
              </span>
            </div>

            <div className="w-20" />
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative py-16 px-4 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-amber-500/10 via-transparent to-transparent dark:from-amber-500/20" />
        
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500/20 dark:bg-amber-500/30 rounded-full mb-6">
            <Crown className="w-5 h-5 text-amber-500 dark:text-amber-400" />
            <span className="text-amber-600 dark:text-amber-300 font-medium">Premium Experience</span>
          </div>
          
          <h1 className="font-playfair text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 dark:text-white mb-6">
            Unlock Your Full
            <span className="text-amber-500 dark:text-amber-400"> Dating Potential</span>
          </h1>
          
          <p className="text-xl text-slate-600 dark:text-gray-300 max-w-2xl mx-auto mb-8">
            Join Elite members who are 3x more likely to find meaningful connections 
            through our premium features and priority matching.
          </p>
          
          {isElite ? (
            <div className="flex flex-col items-center gap-4">
              <div className="flex items-center gap-2 px-6 py-3 bg-green-500/20 border border-green-500/30 rounded-full">
                <Check className="w-5 h-5 text-green-400" />
                <span className="text-green-300 font-medium">You&apos;re an Elite Member!</span>
              </div>
              <button
                onClick={() => navigate('/settings')}
                className="text-gray-400 hover:text-white underline transition-colors"
              >
                Manage Membership
              </button>
            </div>
          ) : (
            <button
              onClick={() => navigate('/shop?focus=elite')}
              className="px-8 py-4 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-slate-900 font-semibold rounded-xl transition-all transform hover:scale-105 shadow-lg shadow-amber-500/25"
            >
              Upgrade to Elite — $19.99/mo
            </button>
          )}
        </div>
      </section>

      {/* Perks Grid */}
      <section className="py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-playfair text-3xl font-bold text-slate-900 dark:text-white text-center mb-12">
            Everything You Get
          </h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {PERKS.map((perk, i) => (
              <div 
                key={i}
                className={`p-6 rounded-2xl border transition-all hover:scale-105 ${
                  perk.highlight 
                    ? 'bg-amber-500/10 border-amber-500/30' 
                    : 'bg-slate-100 dark:bg-white/5 border-slate-200 dark:border-white/10'
                }`}
              >
                <perk.icon className={`w-8 h-8 mb-4 ${
                  perk.highlight ? 'text-amber-500 dark:text-amber-400' : 'text-rose-500 dark:text-rose-400'
                }`} />
                <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">{perk.title}</h3>
                <p className="text-slate-600 dark:text-gray-400 text-sm">{perk.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Comparison */}
      <section className="py-16 px-4 bg-slate-100 dark:bg-slate-900/50">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-playfair text-3xl font-bold text-slate-900 dark:text-white text-center mb-4">
            Choose Your Plan
          </h2>
          <p className="text-slate-600 dark:text-gray-400 text-center mb-12">
            Both plans include all Elite perks. Elite+ gives you more time.
          </p>
          
          <div className="grid md:grid-cols-2 gap-6">
            {TIERS.map((tier, i) => (
              <div 
                key={i}
                className={`relative p-8 rounded-2xl border ${
                  tier.popular 
                    ? 'bg-amber-500/10 border-amber-500/50' 
                    : 'bg-white dark:bg-white/5 border-slate-200 dark:border-white/10'
                }`}
              >
                {tier.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-amber-500 text-slate-900 text-sm font-semibold rounded-full">
                    Most Popular
                  </div>
                )}
                
                <h3 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">{tier.name}</h3>
                <div className="flex items-baseline gap-1 mb-6">
                  <span className="text-4xl font-bold text-slate-900 dark:text-white">{tier.price}</span>
                  <span className="text-slate-500 dark:text-gray-400">{tier.period}</span>
                </div>
                
                <ul className="space-y-3 mb-8">
                  <li className="flex items-center gap-3 text-slate-700 dark:text-gray-300">
                    <Check className="w-5 h-5 text-green-500 dark:text-green-400" />
                    <span>Lounge: {tier.loungeTime}</span>
                  </li>
                  <li className="flex items-center gap-3 text-slate-700 dark:text-gray-300">
                    <Check className="w-5 h-5 text-green-500 dark:text-green-400" />
                    <span>Speed Dating: {tier.speedDating}</span>
                  </li>
                  <li className="flex items-center gap-3 text-slate-700 dark:text-gray-300">
                    <Check className="w-5 h-5 text-green-500 dark:text-green-400" />
                    <span>All Elite perks included</span>
                  </li>
                </ul>
                
                {isElite ? (
                  <button
                    disabled
                    className="w-full py-3 bg-slate-200 dark:bg-white/10 text-slate-500 dark:text-gray-400 rounded-xl cursor-not-allowed"
                  >
                    Current Plan
                  </button>
                ) : (
                  <button
                    onClick={() => navigate(`/shop?focus=${tier.name.toLowerCase().replace('+', '_plus')}`)}
                    className={`w-full py-3 rounded-xl font-semibold transition-all ${
                      tier.popular
                        ? 'bg-amber-500 hover:bg-amber-600 text-slate-900'
                        : 'bg-slate-900 hover:bg-slate-800 dark:bg-white/10 dark:hover:bg-white/20 text-white'
                    }`}
                  >
                    Get {tier.name}
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      {!isElite && (
        <section className="py-16 px-4 bg-gradient-to-t from-amber-500/10 via-transparent to-transparent">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="font-playfair text-3xl font-bold text-slate-900 dark:text-white mb-4">
              Ready to Find Your Person?
            </h2>
            <p className="text-slate-600 dark:text-gray-300 mb-8">
              Build meaningful connections inside structured events and themed video lounges — designed for people who take dating seriously.
            </p>
            <button
              onClick={() => navigate('/shop?focus=elite')}
              className="px-8 py-4 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-slate-900 font-semibold rounded-xl transition-all transform hover:scale-105 shadow-lg shadow-amber-500/25"
            >
              Start Your Elite Journey
            </button>
          </div>
        </section>
      )}
      </div>
    </div>
  );
}
